/**
 * Shutdown weeks — weeks left out of the scheduled dashboard's 12-week OTIF.
 *
 * A shutdown week has little or nothing due, so the few slots that do land
 * dominate its percentage: one late delivery in an otherwise closed week reads
 * as a catastrophic week and drags the 12-week average down for a quarter.
 * Marking the week drops it from that average and breaks the trend line there,
 * rather than scoring a closed factory as a failure.
 *
 * Deliberately narrow: YTD, supplier scorecards and exports still count these
 * weeks, so nobody's standing changes and no historical figure moves.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { CalendarOff, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import { platformSettingsAPI } from '../../../lib/api';

/**
 * Snap any date to the Monday of its week, so a value always matches the
 * dashboard's Mon-Sun buckets however the admin picks it. Parsed as UTC to
 * keep the arithmetic clear of the browser's timezone.
 */
function mondayOf(iso: string): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

function formatWeek(iso: string): string {
  const start = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return iso;
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return `${fmt(start)} – ${fmt(end)} ${start.getUTCFullYear()}`;
}

export function OTIFShutdownWeeksCard() {
  const [weeks, setWeeks] = useState<string[]>([]);
  const [pending, setPending] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    platformSettingsAPI
      .getSettings()
      .then((r) => {
        const raw = (r.data as unknown as Record<string, unknown>)['otif_shutdown_weeks'];
        setWeeks(Array.isArray(raw) ? (raw as string[]) : []);
      })
      .catch(() => toast.error('Could not load shutdown weeks'))
      .finally(() => setLoading(false));
  }, []);

  const add = () => {
    const monday = mondayOf(pending);
    if (!monday) {
      toast.error('Pick a date inside the week you want to exclude');
      return;
    }
    if (weeks.includes(monday)) {
      toast.error(`${formatWeek(monday)} is already excluded`);
      return;
    }
    setWeeks([...weeks, monday].sort());
    setPending('');
  };

  const remove = (w: string) => setWeeks(weeks.filter((x) => x !== w));

  const save = async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        otif_shutdown_weeks: weeks,
      } as unknown as Record<string, unknown>);
      toast.success(
        weeks.length
          ? `${weeks.length} shutdown week${weeks.length === 1 ? '' : 's'} saved`
          : 'Shutdown weeks cleared',
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(
        axiosErr.response?.data?.message ||
          (err instanceof Error ? err.message : 'Could not save shutdown weeks'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5" />
          Shutdown Weeks
        </CardTitle>
        <CardDescription>
          Weeks the site was closed — factory shutdowns, holiday closures. Each one is left out of
          the 12-week OTIF average on the scheduled dashboard and breaks the trend line rather than
          plotting as a low point. Year to date, supplier scorecards and exports still count these
          weeks. Pick any day and it snaps to that week&apos;s Monday.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <label htmlFor="shutdown-week" className="text-sm font-medium">
                  Add a week
                </label>
                <Input
                  id="shutdown-week"
                  type="date"
                  className="w-48"
                  value={pending}
                  onChange={(e) => setPending(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={add} disabled={!pending}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>

            {weeks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No shutdown weeks. All 12 complete weeks count towards the average.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {weeks.map((w) => (
                  <Badge key={w} variant="secondary" className="gap-1.5 py-1 pl-2.5 pr-1.5">
                    {formatWeek(w)}
                    <button
                      type="button"
                      aria-label={`Remove ${formatWeek(w)}`}
                      className="rounded-full p-0.5 hover:bg-muted-foreground/20"
                      onClick={() => remove(w)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Shutdown Weeks'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
